import _ from 'lodash';
import React, { createContext, FunctionComponent, useContext, useReducer } from 'react';

interface DialogsState {
    dialogs: { id: string; dialog: React.ReactNode }[];
    showLoading: boolean;
    priceFormatter: Intl.NumberFormat;
    loadedScripts: string[]; // Changed from isBabylonLoaded to loadedScripts
    user: {
        id: number;
        name: string;
    };
}

type DialogsStateReducer = (state: DialogsState, action: any) => DialogsState;
type DialogsStateDispatch = (action: any) => void;

const initialState: DialogsState = {
    dialogs: [],
    showLoading: false,
    priceFormatter: new Intl.NumberFormat(),
    loadedScripts: [], // Changed from isBabylonLoaded to loadedScripts
    user: { id: 0, name: '' }
};

const context = createContext<{
    state: DialogsState;
    dispatch: DialogsStateDispatch;
}>({
    state: initialState,
    dispatch: () => new Error('Component not wrapped in state provider.')
});

const reducer = (state: DialogsState, action: any) => {
    const newState = { ...state };
    switch (action.type) {
        case 'PRICE_FORMATTER':
            newState.priceFormatter = action.priceFormatter;
            break;
        case 'SHOW_LOADING':
            newState.dialogs = action.showLoading;
            break;
        case 'SHOW_DIALOG':
            {
                const id = (action.id || new Date().getUTCMilliseconds()).toString();
                const cloned = React.cloneElement(action.dialog, { key: id });
                const existingIdx = newState.dialogs.findIndex((x) => x.id === id);
                if (existingIdx >= 0) {
                    // Replace the stored element in place when a dialog with this
                    // id is already open. Callers (e.g. the Step4Validation row
                    // editor) re-invoke showDialog to push fresh props onto an
                    // open dialog after their underlying state changes; the
                    // previous "ignore-if-present" behaviour silently dropped
                    // those updates, leaving the dialog rendered against a
                    // stale snapshot. React reconciles same-key + same-type
                    // children, so the existing component instance is reused
                    // and any local state (open inputs, focus, scroll) is
                    // preserved.
                    newState.dialogs = newState.dialogs.map((d, i) => (i === existingIdx ? { id, dialog: cloned } : d));
                } else {
                    newState.dialogs = [...newState.dialogs, { id, dialog: cloned }];
                }
            }
            break;
        case 'CLOSE_DIALOG':
            newState.dialogs = newState.dialogs.filter((x) => {
                return x.id != action.dialogId;
            });
            break;
        case 'ADD_LOADED_SCRIPT':
            if (!newState.loadedScripts.includes(action.scriptUrl)) {
                newState.loadedScripts = [...newState.loadedScripts, action.scriptUrl];
            }
            break;
        default:
            throw new Error();
    }

    return newState;
};

const DialogsStateProvider: FunctionComponent<{
    children?: React.ReactNode;
}> = ({ children }) => {
    const [state, dispatch] = useReducer(reducer, initialState);
    return <context.Provider value={{ state, dispatch }}>{children}</context.Provider>;
};

const useDialogsState = function () {
    return useContext(context);
};

export { DialogsStateProvider, useDialogsState };
